import React, { useEffect, useState, useMemo, createContext, useContext, ReactNode, ReactElement } from 'react';
import './App.css';
import { BrowserRouter as Router, Routes, Route, Link as RouterLink, Navigate, useNavigate } from 'react-router-dom';
import axios, { AxiosResponse } from 'axios';
import { jwtDecode } from 'jwt-decode';

// --- Material-UI Imports ---
import {
    createTheme, ThemeProvider, CssBaseline, Divider, Container,
    AppBar, Toolbar, Typography, Button, Box, useMediaQuery,
    Menu, MenuItem, IconButton, Paper, TextField, Alert, CircularProgress,
    FormControlLabel, Switch, Link
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import LogoutIcon from '@mui/icons-material/Logout';

// --- Page Component Imports ---
import DailySummaryPage from './pages/DailySummaryPage';
import CampaignsReportPage from './pages/CampaignsReportPage';
import CampaignsPage from './pages/CampaignsPage';
import OffersPage from './pages/OffersPage';
import TrafficSourcesPage from './pages/TrafficSourcesPage';
import CPANetworksPage from './pages/CPANetworksPage';
import DomainsPage from './pages/DomainsPage';
import LandingPagesPage from './pages/LandingPagesPage';
import ConversionsLogPage from './pages/ConversionsLogPage';
import PostbackLogPage from './pages/PostbackLogPage';
import MD5LookupPage from './pages/MD5LookupPage';
import ClickInfoPage from './pages/ClickInfoPage';
import PostbackToolPage from './pages/PostbackToolPage';
import MonitoringPage from './pages/MonitoringPage';
import AngleGeneratorPage from './pages/AngleGeneratorPage';
import OptimizerPage from './pages/OptimizerPage';
import ApiConnectionsPage from './pages/ApiConnectionsPage';
import UpdateCostPage from './pages/UpdateCostPage';
import UsersPage from './pages/UsersPage';
import CampaignEditorPage from './pages/CampaignEditorPage';


// --- Configuración Global ---
export const API_BASE_URL = "https://api.jetadvertising.com.mx";

// --- Tipado para el objeto del usuario decodificado ---
interface UserPayload {
    id: number;
    username: string;
    role: 'admin' | 'user';
}

// --- Tipado para los Contextos ---
interface AuthContextType {
    token: string | null;
    user: UserPayload | null;
    login: (username: string, password: string, rememberMe: boolean) => Promise<AxiosResponse<any, any>>;
    logout: () => void;
}

interface ThemeContextType {
    themeMode: 'light' | 'dark';
    toggleTheme: () => void;
}

// --- 1. CONTEXTO DE TEMA ---
const CustomThemeContext = createContext<ThemeContextType | null>(null);

const CustomThemeProvider = ({ children }: { children: ReactNode }) => {
    const [themeMode, setThemeMode] = useState<'light' | 'dark'>('dark');

    useEffect(() => {
        const savedTheme = localStorage.getItem('theme');
        if (savedTheme === 'light' || savedTheme === 'dark') {
            setThemeMode(savedTheme as 'light' | 'dark');
        }
    }, []);

    const toggleTheme = () => {
        const newTheme = themeMode === 'light' ? 'dark' : 'light';
        setThemeMode(newTheme);
        localStorage.setItem('theme', newTheme);
    };

    const theme = useMemo(() => createTheme({
        palette: {
            mode: themeMode,
            primary: { main: '#36b1d1' },
            secondary: { main: '#d13665' },
            background: {
                default: themeMode === 'dark' ? '#1a1d24' : '#f4f6f8',
                paper: themeMode === 'dark' ? '#282c34' : '#ffffff',
            },
            text: { primary: themeMode === 'dark' ? '#FFFFFF' : '#121212' }
        },
    }), [themeMode]);

    return (
        <CustomThemeContext.Provider value={{ themeMode, toggleTheme }}>
            <ThemeProvider theme={theme}>
                <CssBaseline />
                {children}
            </ThemeProvider>
        </CustomThemeContext.Provider>
    );
};

const useCustomTheme = () => {
    const context = useContext(CustomThemeContext);
    if (!context) {
        throw new Error('useCustomTheme debe ser usado dentro de un CustomThemeProvider');
    }
    return context;
};

// --- 2. CONTEXTO DE AUTENTICACIÓN ---
const AuthContext = createContext<AuthContextType | null>(null);

const AuthProvider = ({ children }: { children: ReactNode }) => {
    const [token, setToken] = useState<string | null>(null);
    const [user, setUser] = useState<UserPayload | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const requestInterceptor = axios.interceptors.request.use(
            config => {
                const currentToken = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
                if (currentToken) {
                    config.headers['Authorization'] = `Bearer ${currentToken}`;
                }
                return config;
            },
            error => Promise.reject(error)
        );
        
        const storedToken = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
        if (storedToken) {
            setToken(storedToken);
            try {
                const decodedUser = jwtDecode<UserPayload>(storedToken);
                setUser(decodedUser);
            } catch (error) {
                console.error("Error decodificando el token:", error);
                setUser(null);
            }
        }
        setLoading(false);

        return () => {
            axios.interceptors.request.eject(requestInterceptor);
        };
    }, []);

    const login = async (username: string, password: string, rememberMe: boolean) => {
        const response = await axios.post(`${API_BASE_URL}/auth/login`, { username, password });
        if (response.data.token) {
            const receivedToken = response.data.token;
            if (rememberMe) {
                localStorage.setItem('authToken', receivedToken);
                sessionStorage.removeItem('authToken'); 
            } else {
                sessionStorage.setItem('authToken', receivedToken);
                localStorage.removeItem('authToken'); 
            }
            setToken(receivedToken);
            try {
                const decodedUser = jwtDecode<UserPayload>(receivedToken);
                setUser(decodedUser);
            } catch (error) {
                console.error("Error decodificando el token:", error);
                setUser(null);
            }
        }
        return response;
    };

    const logout = () => {
        localStorage.removeItem('authToken');
        sessionStorage.removeItem('authToken');
        setToken(null);
        setUser(null);
    };

    const value = useMemo(() => ({
        token,
        user,
        login,
        logout,
    }), [token, user]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth debe ser usado dentro de un AuthProvider');
    }
    return context;
};

// --- 3. RUTA PROTEGIDA ---
const ProtectedRoute = ({ children }: { children: ReactElement }) => {
    const { token } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        if (!token) {
            navigate('/login', { replace: true });
        }
    }, [token, navigate]);

    return token ? children : null;
};

// --- 4. PÁGINA DE LOGIN (CON LOGO Y TEXTO DE COPYRIGHT) ---
const LoginPage = () => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [rememberMe, setRememberMe] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const { login } = useAuth();
    const navigate = useNavigate();

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError(null);
        try {
            await login(username, password, rememberMe);
            navigate('/');
        } catch (err: any) {
            setError(err.response?.data?.message || 'Error al iniciar sesión. Verifique sus credenciales.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Container component="main" maxWidth="xs">
            <Paper elevation={6} sx={{ marginTop: 8, display: 'flex', flexDirection: 'column', alignItems: 'center', padding: 4, borderRadius: '12px' }}>
                <Box
                    component="img"
                    sx={{ height: 50, mb: 2 }}
                    alt="Logo de la empresa."
                    src="https://cdn.jetadvertising.com.mx/jetlogo-stroke-and-fill.png"
                />
                <Typography component="h1" variant="h5">Iniciar Sesión</Typography>
                <Box component="form" onSubmit={handleLogin} noValidate sx={{ mt: 1 }}>
                    <TextField margin="normal" required fullWidth id="username" label="Nombre de Usuario" name="username" autoComplete="username" autoFocus value={username} onChange={(e) => setUsername(e.target.value)} />
                    <TextField margin="normal" required fullWidth name="password" label="Contraseña" type="password" id="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />
                    
                    <FormControlLabel
                        control={
                            <Switch
                                checked={rememberMe}
                                onChange={(e) => setRememberMe(e.target.checked)}
                                name="rememberMe"
                                color="primary"
                            />
                        }
                        label="Recordarme"
                        sx={{ mt: 1, mb: 1 }}
                    />

                    {error && <Alert severity="error" sx={{ mt: 2, width: '100%' }}>{error}</Alert>}
                    <Button type="submit" fullWidth variant="contained" sx={{ mt: 3, mb: 2 }} disabled={isLoading}>
                        {isLoading ? <CircularProgress size={24} /> : 'Ingresar'}
                    </Button>
                </Box>
            </Paper>
            <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 4 }}>
                {"Copyright © JET Advertising"}
            </Typography>
            <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 0.5 }}>
                <Link color="inherit" href="https://jetadvertising.com.mx/" target="_blank" rel="noopener noreferrer" sx={{ textDecoration: 'none' }}>
                    {"jetadvertising.com.mx"}
                </Link>
            </Typography>
        </Container>
    );
};

// --- 5. LAYOUT PRINCIPAL DEL DASHBOARD (MODIFICADO) ---
const DashboardLayout: React.FC<{children: React.ReactNode}> = ({ children }) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const { logout, user } = useAuth();
    const { themeMode, toggleTheme } = useCustomTheme();

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
    const handleMenuClose = () => setAnchorEl(null);

    // --- CAMBIO: Se elimina el enlace a la ruta antigua 'campaigns-edit' ---
    const navLinks = {
        reporting: [ { label: 'Resumen', path: '/daily-summary' }, { label: 'Reportes', path: '/campaigns-report' }, { label: 'Conversiones', path: '/conversions-log' }, { label: 'Postback Logs', path: '/postback-log' }, ],
        management: [ { label: 'Campañas', path: '/campaigns' }, { label: 'Ofertas', path: '/offers' }, { label: 'Fuentes de Tráfico', path: '/traffic-sources' }, { label: 'Redes CPA', path: '/cpa-networks' }, { label: 'Landing Pages', path: '/landing-pages' }, { label: 'Dominios', path: '/domains' }, { label: "Usuarios", path: '/users', adminOnly: true }],
        tools: [ { label: 'Postback', path: '/postback-tool' }, { label: 'Clicks', path: '/click-info' }, { label: 'MD5', path: '/md5-lookup' }, { label: 'Monitor', path: '/monitor', adminOnly: true }, { label: 'AI Angles', path: '/angle-generator', adminOnly: true }, { label: 'Optimizador', path: '/optimizer', adminOnly: true }, { label: 'Conexiones API', path: '/connections', adminOnly: true },{ label: 'Corregir Costos', path: '/update-cost' }],
    };
    
    const allNavItems = useMemo(() => {
        const items = [
            ...navLinks.reporting,
            { type: 'divider', key: 'd1' },
            ...navLinks.management,
            { type: 'divider', key: 'd2' },
            ...navLinks.tools,
        ];

        if (user?.role === 'admin') {
            return items;
        }
        return items.filter(item => !(item as any).adminOnly);
    }, [user]);

    const desktopNavLinks = useMemo(() => {
        const links = [
            { label: 'Resumen', path: '/daily-summary' },
            { label: 'Reportes', path: '/campaigns-report' },
            { label: 'Campañas', path: '/campaigns' },
            { label: 'Ofertas', path: '/offers' },
            { label: 'Landing Pages', path: '/landing-pages' },
            { label: 'Redes Tráfico', path: '/traffic-sources' },
            { label: 'Redes CPA', path: '/cpa-networks' },
            { label: 'Dominios', path: '/domains' },
        ];
        return links;
    }, [user]);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: 'background.default' }}>
            <AppBar position="static" elevation={1}>
                <Toolbar sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <IconButton
                            size="large"
                            edge="start"
                            color="inherit"
                            aria-label="menu"
                            sx={{ mr: 1 }}
                            onClick={handleMenuOpen}
                        >
                            <MenuIcon />
                        </IconButton>

                        <Button color="inherit" onClick={toggleTheme} sx={{ minWidth: '40px', fontSize: '1.2rem', mr: 2 }}>
                            {themeMode === 'light' ? '🌙' : '☀️'}
                        </Button>

                        <RouterLink to="/" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
                            <Box
                                component="img"
                                sx={{ height: 40, cursor: 'pointer' }}
                                alt="Logo de la empresa."
                                src="https://cdn.jetadvertising.com.mx/jetlogo-stroke-and-fill.png"
                            />
                        </RouterLink>
                    </Box>

                    <Box sx={{ display: { xs: 'none', md: 'flex' }, flexGrow: 1, justifyContent: 'center', alignItems: 'center' }}>
                        {desktopNavLinks.map((link) => (
                            <Button
                                key={link.path}
                                color="inherit"
                                component={RouterLink}
                                to={link.path}
                                sx={{ textTransform: 'none', fontWeight: 400, mx: 1 }}
                            >
                                {link.label}
                            </Button>
                        ))}
                    </Box>

                    <Button
                        color="inherit"
                        onClick={logout}
                        startIcon={<LogoutIcon />}
                        sx={{ display: { xs: 'none', md: 'flex' }, ml: 2 }}
                    >
                        Cerrar Sesión
                    </Button>

                    <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
                        {allNavItems.map((item: any) =>
                            'type' in item && item.type === 'divider' ? (
                                <Divider key={item.key} />
                            ) : (
                                <MenuItem key={item.path} onClick={handleMenuClose} component={RouterLink} to={item.path}>
                                    {item.label}
                                </MenuItem>
                            )
                        )}
                        <Divider />
                        <MenuItem onClick={() => { logout(); handleMenuClose(); }}>
                            <LogoutIcon sx={{ mr: 1, fontSize: '1.25rem' }} /> Cerrar Sesión
                        </MenuItem>
                    </Menu>
                </Toolbar>
            </AppBar>
            <Box component="main" sx={{ flexGrow: 1, p: { xs: 1, sm: 2, md: 3 } }}>
                {children}
            </Box>
        </Box>
    );
};

// --- 6. COMPONENTE RAÍZ DE LA APLICACIÓN ---
function App() {
    return (
        <Router>
            <CustomThemeProvider>
                <AuthProvider>
                    <Routes>
                        <Route path="/login" element={<LoginPage />} />
                        <Route path="/*" element={
                            <ProtectedRoute>
                                <DashboardLayout>
                                    <Routes>
                                        <Route path="/" element={<CampaignsReportPage />} />
                                        <Route path="/daily-summary" element={<DailySummaryPage />} />
                                        <Route path="/campaigns-report" element={<CampaignsReportPage />} />
                                        <Route path="/campaigns" element={<CampaignsPage />} />
                                        {/* --- CORRECCIÓN: Se actualizan las rutas para el editor de campañas --- */}
                                        <Route path="/campaigns/new/editor" element={<CampaignEditorPage />} />
                                        <Route path="/campaigns/:campaignId/editor" element={<CampaignEditorPage />} />
                                        <Route path="/offers" element={<OffersPage />} />
                                        <Route path="/traffic-sources" element={<TrafficSourcesPage />} />
                                        <Route path="/cpa-networks" element={<CPANetworksPage />} />
                                        <Route path="/domains" element={<DomainsPage />} />
                                        <Route path="/landing-pages" element={<LandingPagesPage />} />
                                        <Route path="/conversions-log" element={<ConversionsLogPage />} />
                                        <Route path="/postback-log" element={<PostbackLogPage />} />
                                        <Route path="/md5-lookup" element={<MD5LookupPage />} />
                                        <Route path="/click-info" element={<ClickInfoPage />} />
                                        <Route path="/postback-tool" element={<PostbackToolPage />} />
                                        <Route path="/monitor" element={<MonitoringPage />} />
                                        <Route path="/angle-generator" element={<AngleGeneratorPage />} />
                                        <Route path="/optimizer" element={<OptimizerPage />} />
                                        <Route path="/connections" element={<ApiConnectionsPage />} />
                                        <Route path="/update-cost" element={<UpdateCostPage />} />
                                        <Route path="/users" element={<UsersPage />} />
                                        <Route path="*" element={<Navigate to="/campaigns-report" replace />} />
                                    </Routes>
                                </DashboardLayout>
                            </ProtectedRoute>
                        }/>
                    </Routes>
                </AuthProvider>
            </CustomThemeProvider>
        </Router>
    );
}

export default App;
